// Trust clients (CONTRACTS §6c). Browser-uploaded docs are trusted:false, so the
// viewer sandboxes their iframe (no scripts). There are TWO ways to lift that
// sandbox, split by who bears the risk:
//
//   GLOBAL   docs/{docId}.trusted = true — the bundle's JS then runs same-origin
//            for EVERY reader, so this is a decision made on other people's
//            behalf. Owner-only, server-mediated through POST /trust.
//   PERSONAL docs/{docId}/trust/{uid} — un-sandboxes the doc only in the
//            granting user's own browser. The sole session exposed is their own,
//            so any member may grant it to themselves. Written directly via the
//            Web SDK under an own-uid rule (like annotations/comments).
//
// Effective trust in the viewer is `meta.trusted !== false || personalTrusted`;
// personal trust never writes meta.trusted, so it can't leak into another
// reader's rendering.

import {
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "./firebase";

/** Promote (trusted=true) or re-sandbox (trusted=false) a doc for EVERY reader; owner-only. */
export async function setTrusted(
  docId: string,
  trusted = true
): Promise<{ trusted: boolean }> {
  const res = await fetch("/trust", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ docId, trusted }),
    credentials: "same-origin",
  });

  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("Your session expired — sign in again.");
    }
    if (res.status === 403) {
      throw new Error("Only the document owner can trust this document.");
    }
    let message = `Trust update failed (${res.status}).`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data?.error) message = data.error;
    } catch {
      /* keep the generic message */
    }
    throw new Error(message);
  }

  return (await res.json()) as { trusted: boolean };
}

/**
 * Live subscription to whether THIS account has personally trusted the doc.
 * Read is self-only per firestore.rules, so this is never a leak of who else
 * opted in. Errors resolve to `false` (stay sandboxed) — failing closed is the
 * only safe default for a security toggle.
 */
export function watchPersonalTrust(
  docId: string,
  uid: string,
  onData: (trusted: boolean) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, "docs", docId, "trust", uid),
    (snap) => onData(snap.exists()),
    () => onData(false)
  );
}

/** Grant or revoke this account's personal trust for a doc. */
export async function setPersonalTrust(
  docId: string,
  uid: string,
  emailLower: string,
  trusted: boolean
): Promise<void> {
  const ref = doc(db, "docs", docId, "trust", uid);
  if (!trusted) {
    await deleteDoc(ref);
    return;
  }
  await setDoc(ref, {
    uid,
    email: emailLower.toLowerCase(),
    trustedAt: serverTimestamp(),
  });
}
